import { Suspense, lazy, useMemo, useState, useEffect, useRef } from 'react';

import { Button, Drawer, notification, Alert } from 'antd';
import type { TFunction } from 'i18next';
import { Map, Globe, LayoutDashboard, Layers, PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { LanguageSwitcher } from '@/components/i18n/LanguageSwitcher';
import { AppHeaderUserControls } from '@/components/layouts/AppHeaderUserControls';
import { IES_LOGO_SRC } from '@/config/branding';
import { env } from '@/config/env';
import { PATHS } from '@/config/paths';
import { KPIOverlay } from '@/features/dashboard/components/KPIOverlay';
import { useWeatherOperationalMapSync } from '@/features/dashboard/hooks/useWeatherOperationalMapSync';
import { LayerPanel } from '@/features/map/components/LayerPanel';
import { IoTNetworkHeaderPicker } from '@/features/map/components/ui/IoTNetworkHeaderPicker';
import { MapIconLegend } from '@/features/map/components/ui/MapIconLegend';
import { MapTimeSliderOverlay } from '@/features/map/components/ui/MapTimeSliderOverlay';
import {
  CONTENT_INSET_LEFT_PX,
  CONTENT_INSET_RIGHT_PX,
  MAP_EDGE_PADDING_PX,
  MAP_KPI_OVERLAY_TOP_PX,
  MAP_LAYOUT_MOBILE_MAX_PX,
  MAP_LEFT_RAIL_SIDEBAR_TOP_PX,
  MAP_SIDEBAR_LEFT_PX,
  MAP_SIDEBAR_WIDTH_PX,
} from '@/features/map/constants/mapOverlayLayout';
import { FloodAnalysisGridProvider } from '@/features/map/context/FloodAnalysisGridContext';
import {
  MapLeftSidebarHostProvider,
  useMapLeftSidebarHost,
} from '@/features/map/context/MapLeftSidebarHostContext';
import { requestStationFlyTo, setActiveTab } from '@/features/map/stores/mapSlice';
import type { MapTab } from '@/features/map/types/map.types';
import { useGetMyMenuTreeQuery } from '@/features/menus/api/menusApi';
import { DashboardShellMenuLinks } from '@/features/menus/components/DashboardShellMenuLinks';
import { getDynamicOpsLeafPaths } from '@/features/navigation/opsMenuPaths';
import { useDashboardIoTStations } from '@/features/realtime/hooks/useDashboardIoTStations';
import { useIoTThresholdVisitAlerts } from '@/features/realtime/hooks/useIoTThresholdVisitAlerts';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';
import {
  listStationsWithSocketTelemetry,
  type StationWithSocketOverlay,
} from '@/features/realtime/utils/listStationsWithSocketTelemetry';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

const CesiumFloodMap = lazy(() =>
  import('@/features/map/components/CesiumFloodMap').then((module) => ({
    default: module.CesiumFloodMap,
  })),
);
const MultiHazard2DMap = lazy(() =>
  import('@/features/map/components/MultiHazard2DMap').then((module) => ({
    default: module.MultiHazard2DMap,
  })),
);

const LoadingMapFallback = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%',
      background: 'var(--chrome-page-bg)',
      color: 'var(--neon-cyan)',
    }}
  >
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: '3px solid rgba(6, 182, 212, 0.3)',
          borderTopColor: 'var(--neon-cyan)',
          animation: 'spin 1s linear infinite',
        }}
      />
      <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '1px' }}>LOADING MAP...</span>
    </div>
  </div>
);

const WS_DISCONNECT_STATION_LIST_MAX = 6;
const FLY_ZOOM_2D_WS = 14;
const FLY_ZOOM_3D_WS = 15;

/** Tạm ẩn toast “Mất kết nối realtime” / gợi ý trạm live — đặt `true` để bật lại. */
const SHOW_IOT_WS_DISCONNECT_NOTIFICATION = false;

function WsDisconnectNotificationBody({
  rows,
  onFlyTo,
  t,
}: {
  rows: StationWithSocketOverlay[];
  onFlyTo: (station: IoTDataPoint) => void;
  t: TFunction;
}) {
  const shown = rows.slice(0, WS_DISCONNECT_STATION_LIST_MAX);
  const moreCount = Math.max(0, rows.length - WS_DISCONNECT_STATION_LIST_MAX);
  return (
    <div>
      <p
        style={{ margin: '0 0 8px', color: 'rgba(0, 0, 0, 0.75)', fontSize: 13, lineHeight: 1.45 }}
      >
        {t('realtime.wsDisconnectNotify.detail')}
      </p>
      <p
        style={{ margin: '0 0 10px', color: 'rgba(0, 0, 0, 0.65)', fontSize: 13, lineHeight: 1.45 }}
      >
        {t('realtime.wsDisconnectNotify.message')}
      </p>
      {rows.length > 0 ? (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            {t('realtime.wsDisconnectNotify.stationsHeading')}
          </div>
          <ul
            style={{
              margin: 0,
              paddingLeft: 18,
              maxHeight: 168,
              overflowY: 'auto',
            }}
          >
            {shown.map(({ station }) => (
              <li key={station.id} style={{ marginBottom: 4 }}>
                <Button
                  type="link"
                  size="small"
                  aria-label={t('realtime.wsDisconnectNotify.flyLinkAria', {
                    name: station.name ?? station.sensorId,
                  })}
                  style={{ padding: 0, height: 'auto', fontSize: 13, textAlign: 'left' }}
                  onClick={() => onFlyTo(station)}
                >
                  {station.name ?? station.sensorId}
                  {station.name && station.sensorId && station.sensorId !== station.name ? (
                    <span style={{ opacity: 0.75 }}> ({station.sensorId})</span>
                  ) : null}
                </Button>
              </li>
            ))}
          </ul>
          {moreCount > 0 ? (
            <div style={{ fontSize: 12, marginTop: 8, color: 'rgba(0, 0, 0, 0.55)' }}>
              {t('realtime.wsDisconnectNotify.moreCount', { count: moreCount })}
            </div>
          ) : null}
        </>
      ) : (
        <p style={{ margin: 0, fontSize: 12, color: 'rgba(0, 0, 0, 0.6)' }}>
          {t('realtime.wsDisconnectNotify.noLiveHint')}
        </p>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 12px',
        borderRadius: 6,
        border: 'none',
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        transition: 'all 0.2s ease',
        background: active ? 'var(--map-chrome-panel)' : 'transparent',
        color: active ? 'var(--accent-blue)' : 'var(--text-secondary)',
        boxShadow: active
          ? '0 1px 3px rgba(15, 23, 42, 0.12), inset 0 0 0 1px var(--glass-border)'
          : 'none',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

type DashboardMapWorkspaceProps = {
  isMapMobile: boolean;
  showDesktopMapRails: boolean;
  showMobileMapDrawers: boolean;
  mobileMapLeftOpen: boolean;
  setMobileMapLeftOpen: (open: boolean) => void;
  mobileMapRightOpen: boolean;
  setMobileMapRightOpen: (open: boolean) => void;
  cesiumMounted: boolean;
  activeTab: MapTab;
  iotStations: IoTDataPoint[];
};

/**
 * Map stack + rails; left sidebar content is registered by 2D/3D map via {@link useMapLeftSidebarHost}.
 */
function DashboardMapWorkspace({
  isMapMobile,
  showDesktopMapRails,
  showMobileMapDrawers,
  mobileMapLeftOpen,
  setMobileMapLeftOpen,
  mobileMapRightOpen,
  setMobileMapRightOpen,
  cesiumMounted,
  activeTab,
  iotStations,
}: DashboardMapWorkspaceProps) {
  const { t } = useTranslation();
  const { leftSidebarContent } = useMapLeftSidebarHost();

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      {showMobileMapDrawers && (
        <>
          <Drawer
            title={t('dashboard.areaDrawerTitle')}
            placement="left"
            open={mobileMapLeftOpen}
            onClose={() => setMobileMapLeftOpen(false)}
            rootStyle={{ zIndex: 1001 }}
            styles={{ body: { padding: 0 } }}
            width="min(320px, 88vw)"
          >
            {leftSidebarContent}
          </Drawer>
          <Drawer
            title={t('dashboard.layersDrawerTitle')}
            placement="right"
            open={mobileMapRightOpen}
            onClose={() => setMobileMapRightOpen(false)}
            rootStyle={{ zIndex: 1001 }}
            styles={{ body: { padding: 12 } }}
            width="min(300px, 90vw)"
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                height: '100%',
                minHeight: 0,
              }}
            >
              <LayerPanel />
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <MapIconLegend />
              </div>
            </div>
          </Drawer>
        </>
      )}

      {/* 2D Tab */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          visibility: activeTab === '2d' ? 'visible' : 'hidden',
          pointerEvents: activeTab === '2d' ? 'auto' : 'none',
        }}
      >
        <Suspense fallback={<LoadingMapFallback />}>
          <MultiHazard2DMap stations={iotStations} isActive={activeTab === '2d'} />
        </Suspense>

        {showDesktopMapRails && (
          <>
            <div
              style={{
                position: 'absolute',
                top: MAP_LEFT_RAIL_SIDEBAR_TOP_PX,
                left: MAP_SIDEBAR_LEFT_PX,
                bottom: 20,
                width: MAP_SIDEBAR_WIDTH_PX,
                zIndex: 100,
                pointerEvents: 'auto',
              }}
            >
              {leftSidebarContent}
            </div>

            <div
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                bottom: 80,
                zIndex: 100,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                width: 240,
                pointerEvents: 'none',
              }}
            >
              <div style={{ pointerEvents: 'auto', flexShrink: 0 }}>
                <LayerPanel />
              </div>
              <div style={{ pointerEvents: 'auto', flexShrink: 1, minHeight: 0 }}>
                <MapIconLegend />
              </div>
            </div>
          </>
        )}
      </div>

      {cesiumMounted && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            visibility: activeTab === '3d' ? 'visible' : 'hidden',
            pointerEvents: activeTab === '3d' ? 'auto' : 'none',
          }}
        >
          <Suspense fallback={<LoadingMapFallback />}>
            <CesiumFloodMap stations={iotStations} isActive={activeTab === '3d'} />
          </Suspense>

          {showDesktopMapRails && (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: MAP_LEFT_RAIL_SIDEBAR_TOP_PX,
                  left: MAP_SIDEBAR_LEFT_PX,
                  bottom: 20,
                  width: MAP_SIDEBAR_WIDTH_PX,
                  zIndex: 100,
                  pointerEvents: 'auto',
                }}
              >
                {leftSidebarContent}
              </div>

              <div
                style={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  bottom: 80,
                  zIndex: 100,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  width: 240,
                  pointerEvents: 'none',
                }}
              >
                <div style={{ pointerEvents: 'auto', flexShrink: 0 }}>
                  <LayerPanel />
                </div>
                <div style={{ pointerEvents: 'auto', flexShrink: 1, minHeight: 0 }}>
                  <MapIconLegend />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <MapTimeSliderOverlay />

      <div
        style={{
          position: 'absolute',
          top: MAP_KPI_OVERLAY_TOP_PX,
          left: isMapMobile ? MAP_EDGE_PADDING_PX : CONTENT_INSET_LEFT_PX,
          right: isMapMobile ? MAP_EDGE_PADDING_PX : CONTENT_INSET_RIGHT_PX,
          zIndex: 95,
          pointerEvents: 'none',
          display: 'flex',
          justifyContent: 'center',
          overflowX: isMapMobile ? 'auto' : undefined,
        }}
      >
        <KPIOverlay />
      </div>
    </div>
  );
}

export function DashboardLayout() {
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((state) => state.map.activeTab);
  const user = useAppSelector((state) => state.auth.user);
  const scenarioMode = useAppSelector((s) => s.scenario.mode);
  const isLiveScenario = scenarioMode === 'live';
  const operationalApi = shouldFetchOperationalApi();
  const {
    data: menuTree,
    isFetching: menuFetching,
    isError: menuError,
  } = useGetMyMenuTreeQuery(undefined, {
    skip: !user || !env.API_BASE_URL,
  });
  const treeForMerge = menuError ? undefined : menuTree;
  const hasOpsNavFromApi = useMemo(
    () => getDynamicOpsLeafPaths(treeForMerge).length > 0,
    [treeForMerge],
  );
  const opsNavPending = Boolean(env.API_BASE_URL && user && menuFetching);
  const {
    stations: iotStations,
    thresholdRows: iotThresholdRows,
    isLoading: iotStationsLoading,
    stationsForbidden,
  } = useDashboardIoTStations({
    skip: !user || !operationalApi,
  });
  const weatherOpsEnabled = Boolean(user && operationalApi && isLiveScenario);
  useWeatherOperationalMapSync(weatherOpsEnabled);
  useIoTThresholdVisitAlerts(
    iotStations,
    iotThresholdRows,
    Boolean(user && operationalApi && isLiveScenario && !iotStationsLoading && !stationsForbidden),
  );
  const [cesiumMounted, setCesiumMounted] = useState(activeTab === '3d');
  const [mobileMapLeftOpen, setMobileMapLeftOpen] = useState(false);
  const [mobileMapRightOpen, setMobileMapRightOpen] = useState(false);
  const { t } = useTranslation();
  const isMapMobile = useMediaQuery(`(max-width: ${MAP_LAYOUT_MOBILE_MAX_PX}px)`);
  const showDesktopMapRails = !isMapMobile;
  const showMobileMapDrawers = isMapMobile;
  const wsStatus = useAppSelector((s) => s.realtime.status);
  const telemetryByStationId = useAppSelector((s) => s.realtime.telemetryByStationId);
  const wasWsConnectedRef = useRef(false);
  const wsDisconnectWarnedRef = useRef(false);

  const stationWsSyncPayload = useMemo(() => {
    if (stationsForbidden) return { stationIds: [] as string[], basinIds: [] as string[] };
    const stationIdsSet = new Set<string>();
    for (const s of iotStations) {
      const sid = typeof s.id === 'string' ? s.id.trim() : '';
      if (sid) stationIdsSet.add(sid);
      const code = typeof s.sensorId === 'string' ? s.sensorId.trim() : '';
      if (code && code !== sid) stationIdsSet.add(code);
    }
    const stationIds = [...stationIdsSet];
    const basinSet = new Set<string>();
    for (const s of iotStations) {
      const b = s.basinId?.trim();
      if (b) basinSet.add(b);
    }
    return { stationIds, basinIds: [...basinSet] };
  }, [iotStations, stationsForbidden]);

  useEffect(() => {
    if (!user || !operationalApi || !env.WS_URL || !isLiveScenario) return undefined;
    dispatch({ type: 'socket/connect' });
    return () => {
      dispatch({ type: 'socket/disconnect' });
    };
  }, [dispatch, user, operationalApi, isLiveScenario]);

  useEffect(() => {
    if (!user || !operationalApi || !env.WS_URL || !isLiveScenario) return;
    if (wsStatus !== 'connected') return;
    if (iotStationsLoading || stationsForbidden) return;
    dispatch({
      type: 'socket/syncRealtimeSubscriptions',
      payload: stationWsSyncPayload,
    });
  }, [
    dispatch,
    user,
    operationalApi,
    isLiveScenario,
    wsStatus,
    iotStationsLoading,
    stationsForbidden,
    stationWsSyncPayload,
  ]);

  useEffect(() => {
    if (wsStatus === 'connected') {
      wasWsConnectedRef.current = true;
      wsDisconnectWarnedRef.current = false;
    }
  }, [wsStatus]);

  useEffect(() => {
    if (!SHOW_IOT_WS_DISCONNECT_NOTIFICATION) return;
    if (
      wsStatus !== 'disconnected' ||
      !wasWsConnectedRef.current ||
      wsDisconnectWarnedRef.current ||
      !user ||
      !operationalApi ||
      !env.WS_URL ||
      !isLiveScenario
    ) {
      return;
    }
    wsDisconnectWarnedRef.current = true;
    const rows = listStationsWithSocketTelemetry(iotStations, telemetryByStationId);
    const zoom = activeTab === '3d' ? FLY_ZOOM_3D_WS : FLY_ZOOM_2D_WS;
    notification.warning({
      message: t('realtime.wsDisconnectNotify.title'),
      description: (
        <WsDisconnectNotificationBody
          rows={rows}
          onFlyTo={(station) => {
            const [longitude, latitude] = station.position;
            dispatch(requestStationFlyTo({ longitude, latitude, zoom }));
            notification.destroy('iot-ws-disconnected');
          }}
          t={t}
        />
      ),
      duration: rows.length > 0 ? 12 : 6,
      key: 'iot-ws-disconnected',
    });
  }, [
    wsStatus,
    user,
    operationalApi,
    isLiveScenario,
    t,
    iotStations,
    telemetryByStationId,
    activeTab,
    dispatch,
  ]);

  useEffect(() => {
    void LanguageSwitcher;
  }, []);

  function handleTabChange(tab: MapTab) {
    if (tab === '3d') setCesiumMounted(true);
    dispatch(setActiveTab(tab));
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
        overflow: 'hidden',
        background: 'var(--chrome-page-bg)',
        color: 'var(--text-primary)',
      }}
    >
      {/* ════════════════ HEADER ════════════════ */}
      <header
        style={{
          height: 60,
          minHeight: 60,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 20px',
          background: 'var(--chrome-header-bg)',
          borderBottom: '1px solid var(--chrome-header-border)',
          zIndex: 1000,
          backdropFilter: 'blur(20px)',
          boxShadow: 'var(--dashboard-header-shadow)',
          gap: 12,
        }}
      >
        {/* ── Left: Logo + Title ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 10,
              background:
                'linear-gradient(135deg, rgba(6, 182, 212, 0.12), rgba(6, 182, 212, 0.04))',
              border: '1px solid rgba(6, 182, 212, 0.28)',
              boxShadow: '0 0 12px rgba(6, 182, 212, 0.08)',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <img
              src={IES_LOGO_SRC}
              alt={t('auth.logoAlt')}
              width={36}
              height={36}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              decoding="async"
            />
          </div>
          <div>
            <h1
              style={{
                fontSize: 14,
                fontWeight: 800,
                margin: 0,
                color: 'var(--text-primary)',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              {t('dashboard.appTitle')}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#4ade80' }} />
              <p
                style={{
                  fontSize: 10,
                  color: 'var(--text-muted)',
                  margin: 0,
                  fontWeight: 500,
                  letterSpacing: '0.2px',
                }}
              >
                {t('dashboard.appSubtitle')}
              </p>
            </div>
          </div>
          {showMobileMapDrawers && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <button
                type="button"
                aria-label={t('dashboard.openAreaPanel')}
                onClick={() => {
                  setMobileMapLeftOpen(true);
                  setMobileMapRightOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  border: '1px solid var(--glass-border)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                <PanelLeft size={18} strokeWidth={2.2} />
              </button>
              <button
                type="button"
                aria-label={t('dashboard.openLayersPanel')}
                onClick={() => {
                  setMobileMapRightOpen(true);
                  setMobileMapLeftOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  border: '1px solid var(--glass-border)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
              >
                <Layers size={18} strokeWidth={2.2} />
              </button>
            </div>
          )}
        </div>

        <div
          className="custom-scrollbar"
          style={{
            flex: '1 1 0%',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingInline: 8,
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <DashboardShellMenuLinks />
        </div>

        {/* ── Right: Controls (z-index để không bị vùng menu giữa đè khi hẹp màn hình) ── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0,
            position: 'relative',
            zIndex: 2,
          }}
        >
          {user && operationalApi && isLiveScenario && !stationsForbidden && (
            <IoTNetworkHeaderPicker stations={iotStations} />
          )}

          {/* Tab switcher — Maps */}
          <div
            style={{
              display: 'flex',
              gap: 2,
              background: 'var(--bg-secondary)',
              borderRadius: 8,
              padding: '3px',
              border: '1px solid var(--toggle-track-border)',
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            <TabButton
              active={activeTab === '2d'}
              onClick={() => handleTabChange('2d')}
              icon={<Map size={13} strokeWidth={2.5} />}
              label="2D"
            />
            <TabButton
              active={activeTab === '3d'}
              onClick={() => handleTabChange('3d')}
              icon={<Globe size={13} strokeWidth={2.5} />}
              label="3D"
            />
          </div>

          {(hasOpsNavFromApi || opsNavPending) && !env.HIDE_OPS_HEADER_LINK && (
            <>
              <div
                style={{ width: 1, height: 16, background: 'var(--glass-border)', margin: '0 2px' }}
              />
              <Link
                to={PATHS.OPS}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 12px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--glass-border)',
                  background: 'var(--bg-secondary)',
                }}
              >
                <LayoutDashboard size={13} strokeWidth={2.5} />
                {t('dashboard.opsEntry')}
              </Link>
            </>
          )}

          <div style={{ width: 1, height: 20, background: 'var(--glass-border)' }} />

          <AppHeaderUserControls />
        </div>
      </header>

      {user && operationalApi && stationsForbidden ? (
        <Alert
          banner
          type="warning"
          showIcon
          message={t('dashboard.stationsForbidden')}
          style={{
            borderRadius: 0,
            border: 'none',
            borderBottom: '1px solid var(--chrome-header-border)',
          }}
        />
      ) : null}

      {/* ════════════════ MAIN CONTENT ════════════════ */}
      <main style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <MapLeftSidebarHostProvider>
          <FloodAnalysisGridProvider>
            <DashboardMapWorkspace
              isMapMobile={isMapMobile}
              showDesktopMapRails={showDesktopMapRails}
              showMobileMapDrawers={showMobileMapDrawers}
              mobileMapLeftOpen={mobileMapLeftOpen}
              setMobileMapLeftOpen={setMobileMapLeftOpen}
              mobileMapRightOpen={mobileMapRightOpen}
              setMobileMapRightOpen={setMobileMapRightOpen}
              cesiumMounted={cesiumMounted}
              activeTab={activeTab}
              iotStations={iotStations}
            />
          </FloodAnalysisGridProvider>
        </MapLeftSidebarHostProvider>
      </main>
    </div>
  );
}
